import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MousePointer2,
  Eye,
  Send,
  CheckCircle,
  RefreshCw,
  Loader2,
  TrendingUp,
  BarChart3,
  Target,
  Percent
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../App';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const API = '/api';

// Format percentage from decimal
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${(value * 100).toFixed(1)}%`;
};

// Simple bar chart for CTA breakdown
const CTABreakdownChart = ({ data }) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-zinc-500">
        <p>No CTA clicks recorded yet</p>
      </div>
    );
  }
  
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const maxValue = Math.max(...entries.map(([, v]) => v), 1);
  
  return (
    <div className="space-y-3">
      {entries.map(([name, count]) => (
        <div key={name} className="flex items-center gap-3">
          <span className="text-xs text-zinc-400 w-32 text-right truncate" title={name}>
            {name?.replace(/_/g, ' ') || 'Unknown'}
          </span>
          <div className="flex-1 h-6 bg-zinc-800 rounded overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded transition-all duration-500"
              style={{ width: `${(count / maxValue) * 100}%` }}
            />
          </div>
          <span className="text-xs text-zinc-300 w-12 text-right">{count}</span>
        </div>
      ))}
    </div>
  );
};

// Trend chart component
const TrendChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxViews = Math.max(...data.map(d => d.checklist_views || 0), 1);
  const maxClicks = Math.max(...data.map(d => d.cta_clicks || 0), 1);
  const maxPublishes = Math.max(...data.map(d => d.publishes || 0), 1);
  const maxVal = Math.max(maxViews, maxClicks, maxPublishes);
  
  return (
    <div className="mt-4">
      <div className="flex items-center gap-4 mb-2 text-xs text-zinc-500">
        <span className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-blue-500 rounded"></div>
          Views
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-orange-500 rounded"></div>
          Clicks
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-green-500 rounded"></div>
          Publishes
        </span>
      </div>
      <div className="relative h-32 flex items-end gap-0.5">
        {data.map((point, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end group relative">
            <div 
              className="w-full bg-blue-500/60 rounded-t"
              style={{ height: `${(point.checklist_views / maxVal) * 100}%`, minHeight: point.checklist_views > 0 ? '2px' : '0' }}
            />
            <div 
              className="w-full bg-orange-500 rounded-t"
              style={{ height: `${(point.cta_clicks / maxVal) * 100}%`, minHeight: point.cta_clicks > 0 ? '2px' : '0' }}
            />
            <div 
              className="w-full bg-green-500 rounded-t"
              style={{ height: `${(point.publishes / maxVal) * 100}%`, minHeight: point.publishes > 0 ? '2px' : '0' }}
            />
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
              <div className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs whitespace-nowrap">
                <div className="font-medium text-white">{point.date}</div>
                <div className="text-blue-400">Views: {point.checklist_views}</div>
                <div className="text-orange-400">Clicks: {point.cta_clicks}</div>
                <div className="text-green-400">Publishes: {point.publishes}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex mt-1 text-[10px] text-zinc-600">
        <span>{data[0]?.date?.slice(5)}</span>
        <span className="flex-1"></span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

const AdminOnboardingMetrics = () => {
  useDocumentTitle('Onboarding Metrics - Admin');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin && user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/metrics/onboarding?time_range=${timeRange}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching onboarding metrics:', error);
      toast.error('Failed to load onboarding metrics');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6" data-testid="admin-onboarding-metrics-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Target className="w-6 h-6 text-blue-400" />
              Onboarding Metrics
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Track CTA performance and creator conversion funnel
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchMetrics}
              disabled={loading}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Checklist Views */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-5 h-5 text-blue-400" />
                <span className="text-xs text-zinc-500 uppercase">Checklist Views</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {metrics?.events?.checklist_views || 0}
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                {metrics?.unique_users?.viewed_checklist || 0} unique users
              </p>
            </CardContent>
          </Card>

          {/* CTA Clicks */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <MousePointer2 className="w-5 h-5 text-orange-400" />
                <span className="text-xs text-zinc-500 uppercase">CTA Clicks</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {metrics?.events?.cta_clicks || 0}
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                {formatPercent(metrics?.rates?.cta_ctr)} CTR
              </p>
            </CardContent>
          </Card>

          {/* Publish Flow Started */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Send className="w-5 h-5 text-purple-400" />
                <span className="text-xs text-zinc-500 uppercase">Publish Started</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {metrics?.events?.publish_flow_started || 0}
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                Entered publish flow
              </p>
            </CardContent>
          </Card>

          {/* Publish Flow Completed */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-xs text-zinc-500 uppercase">Completed</span>
              </div>
              <div className="text-3xl font-bold text-white">
                {metrics?.events?.publish_flow_completed || 0}
              </div>
              <p className="text-sm text-zinc-500 mt-1">
                {formatPercent(metrics?.rates?.publish_completion_rate)} completion
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Trend Chart */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Daily Onboarding Activity
              </CardTitle>
              <CardDescription>Views, clicks, and publishes over time</CardDescription>
            </CardHeader>
            <CardContent>
              <TrendChart data={metrics?.trend_data || []} />
            </CardContent>
          </Card>

          {/* CTA Breakdown */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-400" />
                CTA Click Breakdown
              </CardTitle>
              <CardDescription>Which CTAs drive the most clicks</CardDescription>
            </CardHeader>
            <CardContent>
              <CTABreakdownChart data={metrics?.cta_breakdown} />
            </CardContent>
          </Card>
        </div>

        {/* Conversion Funnel */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-green-400" />
              Onboarding Funnel
            </CardTitle>
            <CardDescription>Conversion from checklist view to first publish</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center gap-4 py-6">
              {/* Stage 1: Viewed Checklist */}
              <div className="text-center">
                <div className="w-36 h-24 bg-blue-500/20 border border-blue-500/40 rounded-lg flex flex-col items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-400 mb-1" />
                  <span className="text-xl font-bold text-white">{metrics?.unique_users?.viewed_checklist || 0}</span>
                  <span className="text-xs text-zinc-400">Viewed Checklist</span>
                </div>
                <span className="text-xs text-zinc-500 mt-2 block">100%</span>
              </div>

              {/* Arrow */}
              <div className="flex flex-col items-center text-zinc-600">
                <div className="w-8 h-0.5 bg-zinc-700"></div>
                <span className="text-xs mt-1">{formatPercent(metrics?.rates?.cta_ctr)}</span>
              </div>

              {/* Stage 2: Clicked CTA */}
              <div className="text-center">
                <div className="w-32 h-20 bg-orange-500/20 border border-orange-500/40 rounded-lg flex flex-col items-center justify-center">
                  <MousePointer2 className="w-5 h-5 text-orange-400 mb-1" />
                  <span className="text-lg font-bold text-white">{metrics?.events?.cta_clicks || 0}</span>
                  <span className="text-xs text-zinc-400">CTA Clicks</span>
                </div>
                <span className="text-xs text-zinc-500 mt-2 block">{formatPercent(metrics?.rates?.cta_ctr)}</span>
              </div>

              {/* Arrow */}
              <div className="flex flex-col items-center text-zinc-600">
                <div className="w-8 h-0.5 bg-zinc-700"></div>
                <span className="text-xs mt-1">{formatPercent(metrics?.rates?.publish_completion_rate)}</span>
              </div>

              {/* Stage 3: Completed Publish */}
              <div className="text-center">
                <div className="w-28 h-16 bg-green-500/20 border border-green-500/40 rounded-lg flex flex-col items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mb-1" />
                  <span className="text-lg font-bold text-white">{metrics?.unique_users?.completed_publish || 0}</span>
                  <span className="text-xs text-zinc-400">Published</span>
                </div>
                <span className="text-xs text-zinc-500 mt-2 block">{formatPercent(metrics?.rates?.overall_conversion)}</span>
              </div>
            </div>

            {/* Overall Conversion Rate */}
            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg">
                <Percent className="w-4 h-4 text-green-400" />
                <span className="text-zinc-400">Overall Conversion:</span>
                <span className="text-xl font-bold text-green-400">
                  {formatPercent(metrics?.rates?.overall_conversion)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOnboardingMetrics;
